import React from "react";
import Box from "@mui/joy/Box";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import Link from "@mui/joy/Link";
import Table from "@mui/joy/Table";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { AnalyticsFilters, AnalyticsFiltersState } from "../../../bids/analytics/components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    ScrollTable,
    WatchSignals,
} from "../../../bids/analytics/components/AnalyticsShared";
import { HowTo } from "../Shared";
import { AsyncBlockState, useAsyncBlock } from "../../../bids/analytics/hooks";
import { formatInt, formatMoneyKopecks, toNum } from "../../../bids/analytics/format";
import { ChartGranularity } from "../../../../models/bidAnalytics";
import { Game } from "../../../../models/games";
import { FailReasonRow, OrderFailDetailsReport, OrderFailReasonsReport } from "../../../../models/orderAnalytics";
import { ordersFailDetailsApi, ordersFailReasonsApi } from "../ordersAnalyticsApi";
import { DetailsCache, ReasonDetails, SteamCodesTable } from "./FailDetailsBlocks";
import { culpritLabel, failReasonLabel, failReasonListUrl, ReasonSortKey, sortReasonRows } from "../failReasons";
import { toStatsGranularity } from "../summary";
import {
    CulpritBreakdown,
    formatPercent,
    ListContext,
    ListLink,
    ReasonsBarChart,
    ReasonGameHeatmap,
    TopReasonsChart,
} from "./FailReasonsCharts";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

const TOP_CULPRITS = 3;

export function FailReasonsTab({ isActive, filters, onFiltersChange, games, gamesLoading }: Props) {
    const [state, refresh] = useAsyncBlock<OrderFailReasonsReport>(
        () =>
            ordersFailReasonsApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
                granularity: toStatsGranularity(filters.granularity),
            }),
        isActive,
        [filters.from, filters.to, filters.gameId, filters.granularity]
    );
    // Общая детализация: коды Steam и тексты «Без причины»; тексты остальных причин грузятся при раскрытии строки
    const [details, refreshDetails] = useAsyncBlock<OrderFailDetailsReport>(
        () => ordersFailDetailsApi({ from: filters.from, to: filters.to, gameId: filters.gameId }),
        isActive,
        [filters.from, filters.to, filters.gameId]
    );
    const [cache] = React.useState<DetailsCache>(() => new Map());
    const [refreshToken, setRefreshToken] = React.useState(0);
    const report = state.data;
    const isEmpty = report != null && toNum(report.totalFailed) === 0;
    const ctx: ListContext = { gameId: filters.gameId, from: filters.from, to: filters.to };
    const handleRefresh = () => {
        cache.clear();
        setRefreshToken((t) => t + 1);
        refresh();
        refreshDetails();
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={handleRefresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showGranularity
            />
            {state.loading && !report && <BlockLoading />}
            {state.error && <BlockError message={state.error} onRetry={refresh} />}
            {isEmpty && <EmptyState hint="За период нет неуспешных ордеров. Попробуйте сменить период или игру." />}
            {report && !isEmpty && (
                <FailReasonsContent report={report} ctx={ctx} granularity={filters.granularity} details={details}
                                    cache={cache} onDetailsRetry={refreshDetails} refreshToken={refreshToken} />
            )}
        </Box>
    );
}

const HOW_TO_READ = (
    <HowTo items={[
        ["Неуспешные", "предметы ордеров в статусах «Ошибка» (Failed) и «Возврат» (Refunded), созданных в выбранном периоде"],
        ["Причина", "причина неуспеха, которую записал сервис при отмене ордера; «Без причины» — причина не записана"],
        ["Виновник", "сторона, из-за которой сделка не состоялась: продавец, покупатель, платформа (боты), поставщик, администратор"],
        ["Холд и проблемы расширения", "виновник — сторона последнего офера: продавцу — продавец, покупателю — покупатель; холд в P2P — «сторона не определена»"],
        ["Ссылки", "причина и число открывают список этих ордеров в новом окне (у «Без причины» ссылки нет)"],
    ]} />
);

const WATCH_FOR = (
    <WatchSignals items={[
        {
            signal: "Доля продавца",
            growsIsGood: false,
            ifGrows: "продавцы чаще не передают предметы — смотрите причины продавца и тексты ошибок.",
            ifFalls: "продавцы надёжнее, неуспех смещается к другим сторонам.",
        },
        {
            signal: "Доля платформы (боты)",
            growsIsGood: false,
            ifGrows: "наши боты чаще ломают сделки — это наша зона ответственности, разберите тексты ошибок.",
            ifFalls: "боты работают стабильнее.",
        },
        {
            signal: "«Без причины»",
            growsIsGood: false,
            ifGrows: "часть отмен не размечена — аналитика по причинам становится неполной.",
            ifFalls: "причины записываются полнее.",
        },
    ]} />
);

const REASONS_HOW_TO_READ = (
    <HowTo items={[
        ["Сортировка", "нажмите на заголовок столбца; повторное нажатие меняет направление"],
        ["Стрелка в строке", "раскрывает тексты ошибок причины: комментарии ордеров без номеров, ников и дат"],
        ["Текст ошибки", "ссылка на список ордеров, в комментарии которых есть все его слова; полный пример — при наведении"],
        ["«Отклонено продавцом» и «Отмена по запросу продавца»", "разные случаи: в первом продавец отклонил трейд в Steam (или сделал встречное предложение, или отказал в подтверждении 2FA), во втором — сам отменил сделку на сайте"],
    ]} />
);

function FailReasonsContent({ report, ctx, granularity, details, cache, onDetailsRetry, refreshToken }: {
    report: OrderFailReasonsReport;
    ctx: ListContext;
    granularity: ChartGranularity;
    details: AsyncBlockState<OrderFailDetailsReport>;
    cache: DetailsCache;
    onDetailsRetry: () => void;
    refreshToken: number;
}) {
    const renderDetails = (row: FailReasonRow) => (
        <ReasonDetails reason={row.failReason ?? null} ctx={ctx} cache={cache} full={details}
                       onFullRetry={onDetailsRetry} refreshToken={refreshToken} />
    );
    return (
        <>
            <AnalyticsCard title="Неуспешные за период" subtitle="Сколько и по чьей вине" howToRead={HOW_TO_READ}
                           watchFor={WATCH_FOR} snapshotLabel="period">
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                    <MetricHint label="Неуспешные" color="danger" value={formatInt(report.totalFailed)}
                                hint={formatMoneyKopecks(report.totalFailedSum)} />
                    {report.byCulprit.slice(0, TOP_CULPRITS).map((c) => (
                        <MetricHint key={c.culprit} label={culpritLabel(c.culprit)} value={formatInt(c.count)}
                                    hint={`${formatPercent(c.percent)} · ${formatMoneyKopecks(c.sum)}`} />
                    ))}
                </Box>
            </AnalyticsCard>

            <AnalyticsCard
                title="Причины"
                subtitle="Сводная таблица; заголовок столбца — сортировка, стрелка — тексты ошибок причины"
                howToRead={REASONS_HOW_TO_READ}
                snapshotLabel="period"
            >
                <ReasonsTable rows={report.reasons} ctx={ctx} renderDetails={renderDetails} />
            </AnalyticsCard>

            <AnalyticsCard
                title="Коды ошибок Steam"
                subtitle="Коды в скобках из ответов Steam, например «(26)»"
                howToRead={
                    <HowTo items={[
                        ["Код", "число в скобках из ответа Steam; имя — по справочнику EResult Steam"],
                        ["Один код на ордер", "если в комментарии несколько кодов, берётся первый"],
                        ["Чаще всего в причинах", "в каких причинах неуспеха этот код встречается больше всего"],
                    ]} />
                }
                watchFor={
                    <WatchSignals items={[{
                        signal: "Код 26 (Revoked)",
                        growsIsGood: false,
                        ifGrows: "чаще предметы недоступны для обмена — продавцы выставляют то, что уже нельзя передать.",
                        ifFalls: "реже попадаются непередаваемые предметы.",
                    }]} />
                }
                snapshotLabel="period"
            >
                {details.loading && !details.data && <BlockLoading />}
                {details.error && <BlockError message={details.error} onRetry={onDetailsRetry} />}
                {details.data && <SteamCodesTable report={details.data} />}
            </AnalyticsCard>

            <AnalyticsCard
                title="Причины по количеству"
                subtitle="Какие причины дают большую часть неуспеха"
                howToRead={
                    <HowTo items={[
                        ["Столбец", "число неуспешных предметов с этой причиной; причины отсортированы по убыванию"],
                        ["Подпись", "полное название причины; точное число — при наведении на столбец"],
                    ]} />
                }
                snapshotLabel="period"
            >
                <ReasonsBarChart report={report} />
            </AnalyticsCard>

            <AnalyticsCard
                title="Виновники"
                subtitle="Доля каждого виновника; выберите виновника, чтобы увидеть его причины"
                howToRead={
                    <HowTo items={[
                        ["Полоска", "доля виновника среди всех неуспешных; маленькие доли видны по числу и проценту"],
                        ["Выбор виновника", "справа появится список его причин со ссылками на ордера"],
                    ]} />
                }
                snapshotLabel="period"
            >
                <CulpritBreakdown report={report} ctx={ctx} />
            </AnalyticsCard>

            <AnalyticsCard
                title="Причина × игра"
                subtitle="Где какие причины встречаются"
                howToRead={
                    <HowTo items={[
                        ["Ячейка", "число неуспешных предметов с причиной в игре; чем насыщеннее цвет, тем больше"],
                        ["Число", "ссылка на список ордеров этой игры с этой причиной (в новом окне)"],
                    ]} />
                }
                snapshotLabel="period"
            >
                <ReasonGameHeatmap report={report} ctx={ctx} />
            </AnalyticsCard>

            <AnalyticsCard
                title="Динамика топ-5 причин"
                subtitle="Пять самых частых причин за период и остальные"
                howToRead={
                    <HowTo items={[
                        ["Линии", "пять самых частых причин периода и «Прочие» — все остальные"],
                        ["Переключатели", "над графиком скрывают и показывают линии, например «Прочие»"],
                        ["Пропуски", "интервалы без неуспешных показаны нулём"],
                    ]} />
                }
                watchFor={
                    <WatchSignals items={[{
                        signal: "Резкий всплеск одной причины",
                        growsIsGood: false,
                        ifGrows: "скорее всего, сбой: проверьте тексты ошибок этой причины за эти дни.",
                        ifFalls: "причина возвращается к обычному уровню.",
                    }]} />
                }
                snapshotLabel="period"
            >
                <TopReasonsChart report={report} granularity={granularity} />
            </AnalyticsCard>
        </>
    );
}

const COLUMNS: Array<{ key: ReasonSortKey | null; label: string }> = [
    { key: "reason", label: "Причина" },
    { key: null, label: "Виновники" },
    { key: "count", label: "Количество" },
    { key: "percent", label: "% от неуспешных" },
    { key: "sum", label: "Сумма" },
];

function rowKey(row: FailReasonRow): string {
    return String(row.failReason ?? "none");
}

function ReasonsTable({ rows, ctx, renderDetails }: {
    rows: FailReasonRow[];
    ctx: ListContext;
    renderDetails: (row: FailReasonRow) => React.ReactNode;
}) {
    const [sort, setSort] = React.useState<{ key: ReasonSortKey; desc: boolean }>({ key: "count", desc: true });
    const [expanded, setExpanded] = React.useState<ReadonlySet<string>>(new Set());
    const sorted = sortReasonRows(rows, sort.key, sort.desc);

    const toggleExpanded = (key: string) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            if (!next.delete(key)) {
                next.add(key);
            }
            return next;
        });

    const toggle = (key: ReasonSortKey) =>
        setSort((prev) => (prev.key === key ? { key, desc: !prev.desc } : { key, desc: key !== "reason" }));

    return (
        <ScrollTable>
            <Table size="sm" stickyHeader sx={{ minWidth: 960 }}>
                <thead>
                    <tr>
                        <th scope="col" style={{ width: 44 }} aria-label="Тексты ошибок" />
                        {COLUMNS.map((col) => (
                            <th scope="col" key={col.label}
                                aria-sort={col.key && sort.key === col.key ? (sort.desc ? "descending" : "ascending") : undefined}>
                                {col.key
                                    ? (
                                        <Link component="button" type="button" underline="none" color="neutral"
                                              onClick={() => toggle(col.key!)}>
                                            {col.label}
                                            {sort.key === col.key &&
                                                <span aria-hidden="true">{sort.desc ? " ↓" : " ↑"}</span>}
                                        </Link>
                                    )
                                    : col.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((row) => {
                        const key = rowKey(row);
                        const isExpanded = expanded.has(key);
                        return (
                            <React.Fragment key={key}>
                                <tr>
                                    <td>
                                        <IconButton size="sm" variant="plain" aria-expanded={isExpanded}
                                                    aria-label={`Тексты ошибок: ${failReasonLabel(row.failReason)}`}
                                                    onClick={() => toggleExpanded(key)}>
                                            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                        </IconButton>
                                    </td>
                                    <td>
                                        <ListLink url={failReasonListUrl(row.failReason, ctx)}>
                                            {failReasonLabel(row.failReason)}
                                        </ListLink>
                                    </td>
                                    <td>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                            {row.culprits.map((c) => (
                                                <Chip key={c.culprit} size="sm" variant="soft">
                                                    {culpritLabel(c.culprit)} {formatInt(c.count)}
                                                </Chip>
                                            ))}
                                        </Box>
                                    </td>
                                    <td>{formatInt(row.count)}</td>
                                    <td>{formatPercent(row.percentOfFailed)}</td>
                                    <td>{formatMoneyKopecks(row.sum)}</td>
                                </tr>
                                {isExpanded && (
                                    <tr>
                                        <td colSpan={COLUMNS.length + 1} style={{ padding: 8 }}>
                                            {renderDetails(row)}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </Table>
        </ScrollTable>
    );
}
